/**
 * The typed fetch layer and the React Query keys.
 *
 * Response types are imported from the server rather than restated -- one
 * definition, and a query that changes shape is a compile error on the
 * screen that reads it (§4).
 */
import { QueryClient } from '@tanstack/react-query'

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly requestId: string | null,
    readonly reason?: string,
  ) {
    super(message)
    this.name = 'ApiError'
  }
}

type ErrorBody = {
  error?: { code?: string; message?: string; requestId?: string; reason?: string }
}

export async function api<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(path, {
    ...init,
    headers: { Accept: 'application/json', ...init?.headers },
  })

  if (!res.ok) {
    let body: ErrorBody = {}
    try { body = (await res.json()) as ErrorBody } catch {}

    /*
     * A session can end while a tab is open -- it expires, an admin disables
     * the account, a password changes elsewhere. Without this the app keeps
     * rendering as signed-in over a wall of 401s, and the only way out is a
     * reload.
     *
     * An event rather than a direct call: `api` is imported by everything,
     * and importing the auth provider back would be a cycle.
     */
    if (res.status === 401) window.dispatchEvent(new Event('explorer:unauthorized'))

    throw new ApiError(
      res.status,
      body.error?.code ?? 'http_error',
      body.error?.message ?? `${res.status} ${res.statusText}`,
      body.error?.requestId ?? res.headers.get('X-Request-Id'),
      body.error?.reason,
    )
  }
  return (await res.json()) as T
}

/**
 * Query string from a filter selection. Arrays repeat the key rather than
 * joining with commas -- both are accepted by the server, and repeating is
 * what URLSearchParams round-trips losslessly.
 */
export function qs(params: Record<string, string | number | boolean | (string | number)[] | null | undefined>): string {
  const sp = new URLSearchParams()
  for (const [k, v] of Object.entries(params)) {
    if (v === null || v === undefined || v === '') continue
    if (Array.isArray(v)) for (const x of v) sp.append(k, String(x))
    else sp.append(k, String(v))
  }
  const s = sp.toString()
  return s ? `?${s}` : ''
}

/**
 * The data changes only when a pipeline run finishes, so nothing refetches
 * on window focus: a grid that reshuffles when you alt-tab is worse than one
 * that is five minutes stale. A manual refresh lives in the header, and
 * /about shows max(updated_at) so staleness is visible rather than guessed
 * at (§13).
 */
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60_000,
      gcTime: 30 * 60_000,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      retry: (failureCount, error) => {
        // A 4xx will not become a 2xx by asking again.
        if (error instanceof ApiError && error.status < 500) return false
        return failureCount < 2
      },
    },
  },
})

export const keys = {
  health: ['health'] as const,
  filters: (query: string) => ['filters', query] as const,
  companies: (query: string) => ['companies', query] as const,
  company: (symbol: string) => ['company', symbol] as const,
  companyDocuments: (symbol: string, query: string) => ['company', symbol, 'documents', query] as const,
  companyStatements: (symbol: string, query: string) => ['company', symbol, 'statements', query] as const,
  companyNotes: (symbol: string, query: string) => ['company', symbol, 'notes', query] as const,
  companyAuditors: (symbol: string) => ['company', symbol, 'auditors'] as const,
  companyQuality: (symbol: string) => ['company', symbol, 'quality'] as const,
  companyAnnouncements: (symbol: string) => ['company', symbol, 'announcements'] as const,
  companyCrosscheck: (symbol: string) => ['company', symbol, 'crosscheck'] as const,
  document: (id: number) => ['document', id] as const,
  documentPage: (id: number, pageNo: number) => ['document', id, 'page', pageNo] as const,
  documentRaw: (id: number, pageNo: number) => ['document', id, 'page', pageNo, 'raw'] as const,
  documentStatements: (id: number) => ['document', id, 'statements'] as const,
  readiness: (id: number, tier: string) => ['document', id, 'readiness', tier] as const,
  companyGovernance: (symbol: string) => ['company', symbol, 'governance'] as const,
  companyRemuneration: (symbol: string, year: number | null) => ['company', symbol, 'remuneration', year] as const,
  companySubsidiaries: (symbol: string) => ['company', symbol, 'subsidiaries'] as const,
  gaps: (symbol: string) => ['gaps', symbol] as const,
  gapPages: (symbol: string, reason: string) => ['gaps', symbol, 'pages', reason] as const,
  jobs: (symbol?: string) => ['jobs', symbol ?? null] as const,
  job: (id: string) => ['job', id] as const,
  jobLog: (id: string) => ['job', id, 'log'] as const,
  about: ['about'] as const,
}
